import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import publicAxios from '../../utils/publicAxios';
import {
  Typography,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Chip,
  Avatar,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { motion } from 'framer-motion';
import { getUrlFriendlyPersonaId, getUrlFriendlyBlogId, PERSONA_DISPLAY_NAMES } from '../../utils/personaUtils';

const PublicAllBlogsPage = () => {
 
  
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Colors for persona chips - same as private page
  const personaColors = {
    "Restoration Artisan": "#8e44ad", // purple
    "DIYMind": "#3498db", // blue
    "Practical Craftsman": "#2ecc71", // green
    "Fearless Builder": "#e74c3c", // red
    "Everyday Woodsmith": "#f39c12", // orange
    "The Builder Marksman": "#1abc9c", // teal
    "The House Doctor": "#34495e" // dark blue
  };

  useEffect(() => {
    const fetchAllBlogs = async () => {
      try {
        
        setLoading(true);
        
        const response = await publicAxios.get('/public/blogs');
        
        
        if (response.data.blogs && response.data.blogs.length > 0) {
          // Add debug logging to check persona_id values
          
          
          // Shuffle blogs for variety
          const shuffledBlogs = [...response.data.blogs].sort(() => Math.random() - 0.5);
          setBlogs(shuffledBlogs);
          setFilteredBlogs(shuffledBlogs);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching public blogs:', err);
        setError('Failed to load articles. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchAllBlogs();
  }, []);
  
  // Handle search input change
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredBlogs(blogs);
      return;
    }
    
    const term = searchTerm.toLowerCase();
    const filtered = blogs.filter(blog => 
      blog.title?.toLowerCase().includes(term) || 
      blog.blog?.toLowerCase().includes(term) ||
      blog.persona_name?.toLowerCase().includes(term)
    );
    
    setFilteredBlogs(filtered);
  }, [searchTerm, blogs]);

  // Redirect to public blog page
  const handleBlogClick = (blogId) => {
    // Transform to URL-friendly format
    const urlFriendlyBlogId = getUrlFriendlyBlogId(blogId);
    navigate(`/public/blog/${urlFriendlyBlogId}`);
  };
  
  // Redirect to public persona blogs page
  const handlePersonaClick = (personaId) => {
    // Transform to URL-friendly format
    const urlFriendlyPersonaId = getUrlFriendlyPersonaId(personaId);
    navigate(`/public/persona/${urlFriendlyPersonaId}/blogs`);
  };

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            mt: 6,
            textAlign: 'center',
            borderRadius: 2
          }}
        >
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/')}
            sx={{ mt: 2 }}
          >
            Return to Home
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <>
      <Helmet>
        <title>DIY Articles & Guides | DIYMind</title>
        <meta name="description" content="Browse our collection of DIY articles from expert personas. Find guides, tutorials, and inspiration for your home improvement projects." />
        <link rel="canonical" href="https://diymind.ai/public/blogs" />
      </Helmet>
      
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Container maxWidth="lg">
          {/* Back to Home Link */}
          <Box sx={{ display: 'flex', mb: 4 }}>
            <Button
              startIcon={<ArrowBackIosIcon />}
              variant="text"
              onClick={() => navigate('/')}
              sx={{
                color: '#1e3c72',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              Back to DIYMind Home
            </Button>
          </Box>
          
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Typography 
              variant="h1" 
              gutterBottom
              sx={{
                background: 'linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)',
                backgroundClip: 'text',
                textFillColor: 'transparent',
                fontWeight: 700
              }}
            >
              All DIY Articles
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4 }}>
              Explore our complete collection of DIY articles from all our expert personas.
            </Typography>
            
            {/* Search field */}
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Search articles by title, content, or expert..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ mb: 4 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <Divider sx={{ mb: 4 }} />
          </Box>
          
          {filteredBlogs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary">
                {searchTerm ? 'No articles match your search criteria.' : 'No articles available yet.'}
              </Typography>
              {searchTerm && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setSearchTerm('')}
                  sx={{ mt: 3 }}
                >
                  Clear Search
                </Button>
              )}
            </Paper>
          ) : (
            <Grid container spacing={3}>
              {filteredBlogs.map((blog, index) => {
                // Debug logging for each blog item
                
                return (
                  <Grid item xs={12} md={6} lg={4} key={blog.id} sx={{ display: 'flex' }}>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: index * 0.1 }}
                      style={{ width: '100%' }}
                    >
                      <Card 
                        sx={{ 
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          cursor: 'pointer',
                          transition: 'transform 0.3s, box-shadow 0.3s',
                          '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: 6,
                          },
                        }}
                        onClick={() => handleBlogClick(blog.id)}
                      >
                        
                        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                          {/* Persona chip - using same structure as private page */}
                          <Chip
                            avatar={
                              <Avatar 
                                sx={{ 
                                  bgcolor: personaColors[blog.persona_id] || 'primary.main',
                                }}
                              >
                                {blog.persona_id?.charAt(0)?.toUpperCase() || 'A'}
                              </Avatar>
                            }
                            label={PERSONA_DISPLAY_NAMES[blog.persona_id] || blog.persona_id}
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePersonaClick(blog.persona_id);
                            }}
                            sx={{ 
                              mb: 2,
                              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }
                            }}
                          />
                          
                          {/* Title with fixed height */}
                          <Typography 
                            gutterBottom 
                            variant="h5" 
                            component="div"
                            sx={{ 
                              minHeight: '64px',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}
                          >
                            {blog.title}
                          </Typography>
                          
                          {/* Text content with fixed height */}
                          <Typography 
                            variant="body2" 
                            color="text.secondary"
                            sx={{ 
                              flexGrow: 1,
                              display: '-webkit-box',
                              WebkitLineClamp: 4,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              mb: 2
                            }}
                          >
                            {blog.blog ? blog.blog.substring(0, 150) + (blog.blog.length > 150 ? '...' : '') : 'Click to read this article'}
                          </Typography>
                        </CardContent>
                        
                        <CardActions sx={{ p: 2, mt: 'auto' }}>
                          <Button 
                            size="small" 
                            variant="contained"
                            sx={{
                              boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                              '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(0,0,0,0.2)'
                              },
                              transition: 'all 0.3s'
                            }}
                          >
                            Read Article
                          </Button>
                        </CardActions>
                      </Card>
                    </motion.div>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Container>
      </Box>
    </>
  );
};

export default PublicAllBlogsPage;

